/* iter55z-j — BookmarksDialog: list, create, edit, delete bookmarks.
 *
 * Supports two bookmark kinds:
 *   - "page":      pinned to a page number
 *   - "selection": pinned to an anchor text passage
 *
 * The user can toggle a bookmark's `is_shared` flag to make it visible
 * to co-editors. Own bookmarks are always visible; other collaborators'
 * bookmarks appear only when they are shared.
 *
 * Props:
 *   open, onOpenChange
 *   projectId, currentChapterId, currentPageNumber, currentSelection
 *   currentUserId    (to distinguish own vs. shared)
 *   onJumpToChapter(chapter_id)
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Bookmark, Trash2, Users, User, Plus, Loader2 } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

export default function BookmarksDialog({
  open, onOpenChange,
  projectId, currentChapterId, currentPageNumber, currentSelection,
  currentUserId, onJumpToChapter,
}) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [newShared, setNewShared] = useState(false);

  const load = useCallback(async () => {
    if (!projectId) return;
    setBusy(true);
    try {
      const r = await axios.get(`${API}/bookmarks`, {
        params: { project_id: projectId }, withCredentials: true,
      });
      setItems(r.data || []);
    } catch {
      setItems([]);
    } finally { setBusy(false); }
  }, [projectId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const canAddSelection = !!(currentSelection && currentSelection.length > 0);
  const canAddPage      = !!(currentChapterId && currentPageNumber);

  const doCreate = async (kind) => {
    if (creating) return;
    if (kind === 'selection' && !canAddSelection) return;
    if (kind === 'page'      && !canAddPage)      return;
    setCreating(true);
    try {
      await axios.post(`${API}/bookmarks`, {
        project_id:  projectId,
        chapter_id:  currentChapterId,
        kind,
        page_number: kind === 'page' ? currentPageNumber : null,
        anchor_text: kind === 'selection' ? currentSelection : null,
        note:        newNote.trim(),
        is_shared:   newShared,
        color:       'gold',
      }, { withCredentials: true });
      setNewNote(''); setNewShared(false);
      await load();
      toast.success('Bookmark saved.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save bookmark.');
    } finally { setCreating(false); }
  };

  const doDelete = async (bid) => {
    try {
      await axios.delete(`${API}/bookmarks/${bid}`, { withCredentials: true });
      setItems((prev) => prev.filter((b) => b.id !== bid));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not delete.');
    }
  };

  const toggleShared = async (bm) => {
    try {
      const r = await axios.patch(`${API}/bookmarks/${bm.id}`,
        { is_shared: !bm.is_shared }, { withCredentials: true });
      setItems((prev) => prev.map((b) => b.id === bm.id ? { ...b, ...r.data } : b));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="editor-bookmarks-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bookmark className="h-4 w-4 text-primary" /> Bookmarks
          </DialogTitle>
          <DialogDescription>
            Save a spot in the manuscript by page number or by a specific selection.
            Toggle the share icon to let co-editors see your bookmark.
          </DialogDescription>
        </DialogHeader>

        {/* Create row */}
        <div className="rounded-md border border-panel-border bg-secondary/30 p-3" data-testid="bookmark-create-row">
          <div className="mb-2 text-[10.5px] uppercase tracking-wider text-muted-foreground">Add a bookmark</div>
          {canAddSelection && (
            <div className="mb-2 rounded-sm border border-panel-border bg-card p-2 text-[11.5px] italic text-foreground/85" data-testid="bookmark-selection-preview">
              &ldquo;{currentSelection.length > 140 ? currentSelection.slice(0, 140) + '…' : currentSelection}&rdquo;
            </div>
          )}
          <Textarea
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            placeholder="Optional note (why this matters, what to come back to…)"
            className="mb-2 min-h-[60px] text-[13px]"
            data-testid="bookmark-note-input"
          />
          <div className="flex items-center gap-2">
            <label className="flex cursor-pointer items-center gap-1.5 text-[11px] text-muted-foreground">
              <input
                type="checkbox"
                checked={newShared}
                onChange={(e) => setNewShared(e.target.checked)}
                data-testid="bookmark-share-checkbox"
              />
              Share with co-editors
            </label>
            <div className="ml-auto flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={!canAddPage || creating}
                onClick={() => doCreate('page')}
                data-testid="bookmark-add-page-btn"
                className="h-8 gap-1.5 text-[11px]"
                title={canAddPage ? `Bookmark page ${currentPageNumber}` : 'Open a chapter first'}
              >
                <Plus className="h-3.5 w-3.5" /> Page {currentPageNumber || '—'}
              </Button>
              <Button
                size="sm"
                disabled={!canAddSelection || creating}
                onClick={() => doCreate('selection')}
                data-testid="bookmark-add-selection-btn"
                className="h-8 gap-1.5 text-[11px]"
                title={canAddSelection ? 'Bookmark the current selection' : 'Highlight text in the manuscript first'}
              >
                {creating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                Selection
              </Button>
            </div>
          </div>
        </div>

        {/* List */}
        <div className="scroll-thin max-h-[45vh] space-y-2 overflow-y-auto pr-1" data-testid="bookmarks-list">
          {busy && <p className="text-[12px] text-muted-foreground">Loading…</p>}
          {!busy && items.length === 0 && (
            <p className="rounded-md border border-dashed border-panel-border/60 p-6 text-center text-[12px] text-muted-foreground" data-testid="bookmarks-empty">
              No bookmarks yet. Add one from your current chapter.
            </p>
          )}
          {items.map((bm) => {
            const isOwn = bm.user_id === currentUserId;
            return (
              <div
                key={bm.id}
                className="rounded-md border border-panel-border bg-card p-2.5"
                data-testid={`bookmark-item-${bm.id}`}
              >
                <div className="flex items-start gap-2">
                  <Bookmark className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${bm.is_shared ? 'text-amber-500' : 'text-primary'}`} />
                  <div className="flex-1">
                    <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">
                      {bm.kind === 'page' ? `Page ${bm.page_number}` : 'Selection'}
                      {' · '}
                      {isOwn ? 'yours' : 'shared'}
                    </div>
                    {bm.anchor_text && (
                      <div className="mt-0.5 text-[12px] italic text-foreground/85">
                        &ldquo;{bm.anchor_text.slice(0, 200)}{bm.anchor_text.length > 200 ? '…' : ''}&rdquo;
                      </div>
                    )}
                    {bm.note && (
                      <div className="mt-1 text-[12px] text-foreground/90">
                        {bm.note}
                      </div>
                    )}
                    <div className="mt-1 flex items-center gap-2 text-[10px] text-muted-foreground">
                      <span>{new Date(bm.created_at).toLocaleString()}</span>
                      {bm.chapter_id !== currentChapterId && onJumpToChapter && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 px-2 text-[10px]"
                          onClick={() => { onJumpToChapter(bm.chapter_id); onOpenChange(false); }}
                          data-testid={`bookmark-jump-${bm.id}`}
                        >
                          Jump to chapter
                        </Button>
                      )}
                    </div>
                  </div>
                  {isOwn && (
                    <div className="flex flex-col gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0"
                        onClick={() => toggleShared(bm)}
                        title={bm.is_shared ? 'Make private' : 'Share with co-editors'}
                        data-testid={`bookmark-share-toggle-${bm.id}`}
                      >
                        {bm.is_shared ? <Users className="h-3.5 w-3.5" /> : <User className="h-3.5 w-3.5" />}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                        onClick={() => doDelete(bm.id)}
                        title="Delete bookmark"
                        data-testid={`bookmark-delete-${bm.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
